import { z } from 'zod';
import type { ShareCardConfig, ShareCardPattern } from '@tabbio/types';

/**
 * Per-CV "share card": the customizable Open Graph link-unfurl preview
 * (LinkedIn/WhatsApp/iMessage) and the in-app share card preview.
 *
 * This module is the single source of truth for the theme catalog, the
 * silhouette list, the call-to-action model, the card-link rule and the
 * free/paid matrix, so the web OG image (satori), the native preview and the
 * web preview all render identically and the API coerces the same way the
 * editor gates. Silhouette geometry lives in `share-card-art.ts`.
 *
 * Gradient themes are rasterised (apps/web/scripts/render-share-themes.mjs)
 * because neither satori nor the native card can draw the design's layered
 * radial gradients; `css` carries the design CSS for web swatches/fallbacks.
 */

export const SHARE_CARD_TITLE_MAX = 60;
export const SHARE_CARD_DESC_MAX = 110;
export const SHARE_CARD_CTA_MAX = 28;
export const SHARE_CARD_URL_MAX = 200;
export const SHARE_CARD_DEFAULT_DESCRIPTION = 'Tap to view and download my CV.';

/** The call-to-action presets. "View CV" is the default and comes first. */
export const SHARE_CARD_DEFAULT_CTA = 'View CV';
export const SHARE_CARD_DOWNLOAD_CTA = 'Download CV';
export const SHARE_CARD_FREE_CTAS: readonly string[] = [
  SHARE_CARD_DEFAULT_CTA,
  SHARE_CARD_DOWNLOAD_CTA,
];

// ─── Themes ──────────────────────────────────────────────────

export type ShareCardThemeId =
  | 'white'
  | 'black'
  | 'tabbio'
  | 'custom'
  | 'aurora'
  | 'dusk'
  | 'uae'
  | 'saudi'
  | 'qatar'
  | 'kuwait'
  | 'bahrain'
  | 'oman';

/** Picker order. */
export const SHARE_CARD_THEME_IDS: readonly ShareCardThemeId[] = [
  'white',
  'black',
  'tabbio',
  'custom',
  'aurora',
  'dusk',
  'uae',
  'saudi',
  'qatar',
  'kuwait',
  'bahrain',
  'oman',
];

/** Themes drawn from rendered artwork (`tabbio` = share-card-bg, the rest = og/themes/<id>). */
export type ShareCardArtKey = Exclude<ShareCardThemeId, 'white' | 'black' | 'custom'>;
export const SHARE_CARD_ART_KEYS: readonly ShareCardArtKey[] = [
  'tabbio',
  'aurora',
  'dusk',
  'uae',
  'saudi',
  'qatar',
  'kuwait',
  'bahrain',
  'oman',
];

/** Gradient themes rendered by the theme script (everything with art except the Tabbio one). */
export const SHARE_CARD_GRADIENT_THEME_IDS: readonly Exclude<ShareCardArtKey, 'tabbio'>[] = [
  'aurora',
  'dusk',
  'uae',
  'saudi',
  'qatar',
  'kuwait',
  'bahrain',
  'oman',
];

export type ShareCardThemeKind = 'solid' | 'art' | 'custom';

/** Soft inks shared by the link line (the CTA's frosted white, and its dark twin). */
export const SHARE_CARD_LINK_INK_DARK = 'rgba(23, 23, 25, 0.78)';
export const SHARE_CARD_LINK_INK_LIGHT = 'rgba(255, 255, 255, 0.78)';
const ORBIT_DARK = 'rgba(23, 23, 25, 0.063)';
const ORBIT_LIGHT = 'rgba(255, 255, 255, 0.078)';

export interface ShareCardTheme {
  id: ShareCardThemeId;
  kind: ShareCardThemeKind;
  /** Rendered artwork key, when the theme is drawn from an image. */
  artKey: ShareCardArtKey | null;
  /** CSS background: the solid colour, or the design gradient (web swatch/fallback). */
  css: string;
  /** One flat colour standing in where only a colour can be drawn. */
  fallbackColor: string;
  /** Identity ink (name, headline, silhouettes, the edit control's glass). */
  ink: string;
  /** Link line ink (favicon + URL): one soft tone. */
  linkInk: string;
  /** The edit control's brush (app only): the ink, or white where the top-end corner is dark. */
  editInk: string;
  /** Orbit ring colour. */
  orbitColor: string;
  /** Hairline inset ring, when the theme draws one. */
  ring: string | null;
  /** Light foreground on a dark field. */
  isDark: boolean;
  /** Needs `individual.custom_branding` to save. */
  pro: boolean;
  /** GCC national palettes: blurred field, quiet silhouettes, soft white ring. */
  gcc: boolean;
}

/** CSS of the design repo's gradient themes (share-gradients.css). */
export const SHARE_CARD_THEME_CSS: Record<Exclude<ShareCardArtKey, 'tabbio'>, string> = {
  aurora:
    'radial-gradient(ellipse at 95% 0%, #b5dfd7 0%, transparent 64%), radial-gradient(ellipse at 15% 100%, #c6b9f1 0%, transparent 62%), linear-gradient(125deg, #f2e9fa, #e7f0f8)',
  dusk:
    'radial-gradient(ellipse at 100% 0%, #3c366d 0%, transparent 65%), radial-gradient(ellipse at 0% 100%, #334c61 0%, transparent 65%), linear-gradient(125deg, #202936, #30273e)',
  uae:
    'radial-gradient(ellipse 38% 135% at -3% 48%, #C92C43 0%, #C92C43E6 28%, #C92C4300 92%), radial-gradient(ellipse 120% 92% at 76% -30%, #145F45 0%, #145F45D9 38%, #145F4500 94%), radial-gradient(ellipse 110% 92% at 90% 130%, #1C2630 0%, #1C2630D9 35%, #1C263000 96%), linear-gradient(120deg, #F4F3EE, #E6EBE7)',
  saudi:
    'radial-gradient(ellipse 95% 32% at 60% 52%, #F0F5E940 0%, #E2EEDF33 22%, #E2EEDF00 80%), radial-gradient(ellipse 62% 120% at 112% -20%, #E7F0E6 0%, #C4D8C56B 28%, #C4D8C500 78%), radial-gradient(ellipse 95% 130% at 70% 115%, #2D8062 0%, #2D806200 82%), linear-gradient(115deg, #0B4331, #17634A)',
  qatar:
    'radial-gradient(ellipse 70% 105% at 105% -25%, #A95070 0%, #A9507000 85%), linear-gradient(90deg, #F5F0EA 0%, #F2EAE6 22%, #B67288 24%, #7D2345 28%, #78203F 68%, #8E3152 100%)',
  kuwait:
    'radial-gradient(ellipse 38% 135% at -3% 48%, #202B31 0%, #202B31D9 28%, #202B3100 92%), radial-gradient(ellipse 120% 92% at 76% -30%, #1E6C4E 0%, #1E6C4ED9 38%, #1E6C4E00 94%), radial-gradient(ellipse 110% 92% at 90% 130%, #B63249 0%, #B63249D9 35%, #B6324900 96%), linear-gradient(120deg, #F3F3EF, #E5EAE7)',
  bahrain:
    'radial-gradient(ellipse 70% 105% at 105% -25%, #E06E66 0%, #E06E6600 85%), linear-gradient(90deg, #F7F1E9 0%, #F3EAE4 22%, #D48A88 24%, #B72F40 28%, #AB2939 68%, #CB4951 100%)',
  oman:
    'radial-gradient(ellipse 36% 150% at -5% 50%, #B83141 0%, #B83141D9 28%, #B8314100 92%), radial-gradient(ellipse 85% 105% at 115% 40%, #C94E5159 0%, #C94E5100 80%), radial-gradient(ellipse 115% 105% at 85% 145%, #1C664A 0%, #1C664AE6 38%, #1C664A00 94%), linear-gradient(120deg, #F5EEE4, #EFE1D9)',
};

const WHITE_RING = '#e8e8ed';
const GCC_RING = 'rgba(255, 255, 255, 0.14)';

function theme(
  id: ShareCardThemeId,
  kind: ShareCardThemeKind,
  css: string,
  fallbackColor: string,
  dark: boolean,
  options: {
    pro?: boolean;
    gcc?: boolean;
    ring?: string | null;
    linkInk?: string;
    editInk?: string;
  } = {},
): ShareCardTheme {
  const ink = dark ? '#ffffff' : '#171719';
  return {
    id,
    kind,
    artKey: kind === 'art' ? (id as ShareCardArtKey) : null,
    css,
    fallbackColor,
    ink,
    linkInk: options.linkInk ?? (dark ? SHARE_CARD_LINK_INK_LIGHT : SHARE_CARD_LINK_INK_DARK),
    editInk: options.editInk ?? ink,
    orbitColor: dark ? ORBIT_LIGHT : ORBIT_DARK,
    ring: options.ring ?? null,
    isDark: dark,
    pro: options.pro ?? true,
    gcc: options.gcc ?? false,
  };
}

/**
 * The theme catalog, in picker order. Inks follow the design repo
 * (share-card-theme.js / share-gradients.css): the link line is one soft
 * dark tone everywhere except black, dusk, saudi and tabbio, which use the
 * soft white; qatar and bahrain carry light identity text over their dark
 * fields but keep the dark link ink over their pearl-white start. The edit
 * brush is white on uae and kuwait, whose top-end corner is a dark green.
 */
export const SHARE_CARD_THEMES: readonly ShareCardTheme[] = [
  theme('white', 'solid', '#ffffff', '#ffffff', false, { pro: false, ring: WHITE_RING }),
  theme('black', 'solid', '#171719', '#171719', true, { pro: false }),
  theme('tabbio', 'art', 'linear-gradient(135deg, #8dbcff, #7657f6)', '#7657f6', true, { pro: false }),
  theme('custom', 'custom', '#7657f6', '#7657f6', true),
  theme('aurora', 'art', SHARE_CARD_THEME_CSS.aurora, '#eaecf7', false),
  theme('dusk', 'art', SHARE_CARD_THEME_CSS.dusk, '#283040', true),
  theme('uae', 'art', SHARE_CARD_THEME_CSS.uae, '#e9ebe6', false, {
    gcc: true,
    ring: GCC_RING,
    editInk: '#ffffff',
  }),
  theme('saudi', 'art', SHARE_CARD_THEME_CSS.saudi, '#11533d', true, { gcc: true, ring: GCC_RING }),
  theme('qatar', 'art', SHARE_CARD_THEME_CSS.qatar, '#7d2345', true, {
    gcc: true,
    ring: GCC_RING,
    linkInk: SHARE_CARD_LINK_INK_DARK,
  }),
  theme('kuwait', 'art', SHARE_CARD_THEME_CSS.kuwait, '#e8ebe7', false, {
    gcc: true,
    ring: GCC_RING,
    editInk: '#ffffff',
  }),
  theme('bahrain', 'art', SHARE_CARD_THEME_CSS.bahrain, '#b02f3d', true, {
    gcc: true,
    ring: GCC_RING,
    linkInk: SHARE_CARD_LINK_INK_DARK,
  }),
  theme('oman', 'art', SHARE_CARD_THEME_CSS.oman, '#f0e6dc', false, { gcc: true, ring: GCC_RING }),
];

const THEME_BY_ID = new Map(SHARE_CARD_THEMES.map((t) => [t.id, t]));

export function getShareCardTheme(id: ShareCardThemeId): ShareCardTheme {
  return THEME_BY_ID.get(id)!;
}

/** The default appearance for a card that was never customised. */
export const SHARE_CARD_DEFAULT_BACKGROUND: ShareCardThemeId = 'tabbio';
export const SHARE_CARD_DEFAULT_PATTERN: ShareCardPattern = 'signature';
export const SHARE_CARD_DEFAULT_CUSTOM_COLOR = '#7657f6';

export const SHARE_CARD_FREE_THEMES: readonly ShareCardThemeId[] = ['white', 'black', 'tabbio'];

/** Old ids that still resolve. `violet` was the free default and is now `tabbio`. */
const THEME_ALIASES: Record<string, ShareCardThemeId> = { violet: 'tabbio' };

/**
 * Pro gradients from the previous catalog. They are not offered in the picker
 * any more but a saved choice keeps rendering its own gradient.
 */
export const LEGACY_SHARE_CARD_GRADIENTS: Readonly<Record<string, { css: string; isDark: boolean }>> = {
  royal: { css: 'linear-gradient(150deg, #B79BFF 0%, #7B4FE6 52%, #4319A6 100%)', isDark: true },
  indigo: { css: 'linear-gradient(150deg, #A9B4FB 0%, #6A6FE6 52%, #3B2FB8 100%)', isDark: true },
  blue: { css: 'linear-gradient(150deg, #9FC2FA 0%, #5B8DEF 52%, #1E4FD6 100%)', isDark: true },
  night: { css: 'linear-gradient(150deg, #8AA6B8 0%, #3C5A6E 52%, #14222E 100%)', isDark: true },
  ink: { css: 'linear-gradient(150deg, #9AA3B2 0%, #4A5263 55%, #1C2230 100%)', isDark: true },
  emerald: { css: 'linear-gradient(150deg, #9FE3C4 0%, #3FB985 52%, #0E7A55 100%)', isDark: true },
  coral: { css: 'linear-gradient(150deg, #FBB9A6 0%, #F07F63 52%, #D9482E 100%)', isDark: true },
  sunset: { css: 'linear-gradient(150deg, #FBC0A0 0%, #F0728C 52%, #C2379E 100%)', isDark: true },
  sand: { css: 'linear-gradient(150deg, #F4ECDD 0%, #E2CFA8 52%, #C2A56E 100%)', isDark: false },
};

// ─── Backwards-compatible catalog view ───────────────────────

export type ShareCardBackgroundKind = 'solid' | 'gradient';

export interface ShareCardBackgroundPreset {
  id: string;
  kind: ShareCardBackgroundKind;
  /** CSS background value: a colour or a gradient. */
  css: string;
  /** Whether foreground text/elements should render light (on dark backgrounds). */
  isDark: boolean;
  /** Pro-gated. */
  pro: boolean;
}

/**
 * The themes as a flat preset list (every theme but `custom`, which the
 * editor offers through its own colour control). Kept for the existing
 * Customize sheets; new code should read SHARE_CARD_THEMES.
 */
export const SHARE_CARD_BACKGROUNDS: readonly ShareCardBackgroundPreset[] = SHARE_CARD_THEMES.filter(
  (t) => t.kind !== 'custom',
).map((t) => ({
  id: t.id,
  kind: t.kind === 'solid' ? 'solid' : 'gradient',
  css: t.css,
  isDark: t.isDark,
  pro: t.pro,
}));

// ─── Custom colour ───────────────────────────────────────────

const CUSTOM_PREFIX = 'custom:';
const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function expandHex(hex: string): string | null {
  let c = hex.replace('#', '').trim();
  if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
  if (!/^[0-9a-fA-F]{6}$/.test(c)) return null;
  return `#${c.toLowerCase()}`;
}

/** WCAG relative luminance of a 6-digit hex colour. */
export function hexLuminance(hex: string): number {
  const full = expandHex(hex);
  if (!full) return 0;
  const channels = full
    .slice(1)
    .match(/../g)!
    .map((n) => parseInt(n, 16) / 255)
    .map((n) => (n <= 0.04045 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4));
  return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
}

/**
 * The readable ink for a custom colour: black or white, whichever has the
 * higher WCAG contrast (always >= 4.5 against one of them). Mirrors the
 * design repo's `cardInk`.
 */
export function shareCardCustomInk(hex: string): '#000000' | '#ffffff' {
  const l = hexLuminance(hex);
  return (l + 0.05) / 0.05 >= 1.05 / (l + 0.05) ? '#000000' : '#ffffff';
}

/** Luminance check kept for callers that only need a dark/light hint. */
export function isHexDark(hex: string): boolean {
  return shareCardCustomInk(hex) === '#ffffff';
}

/** Build a `custom:#hex` background value from a raw hex colour. */
export function buildCustomBackground(hex: string): string {
  return `${CUSTOM_PREFIX}${hex}`;
}

export function isCustomBackground(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.startsWith(CUSTOM_PREFIX);
}

/** The hex of a `custom:#hex` value, or null when it is not a valid custom colour. */
export function customBackgroundHex(value: string | null | undefined): string | null {
  if (!isCustomBackground(value)) return null;
  const hex = (value as string).slice(CUSTOM_PREFIX.length);
  return HEX_RE.test(hex) ? expandHex(hex) : null;
}

// ─── Theme resolution ────────────────────────────────────────

export interface ResolvedShareCardTheme {
  /** Catalog id; a legacy Pro gradient resolves to `legacy`. */
  id: ShareCardThemeId | 'legacy';
  kind: ShareCardThemeKind | 'legacy';
  artKey: ShareCardArtKey | null;
  /** CSS background: a solid colour or a gradient string. */
  css: string;
  fallbackColor: string;
  ink: string;
  linkInk: string;
  editInk: string;
  orbitColor: string;
  ring: string | null;
  isDark: boolean;
  gcc: boolean;
  pro: boolean;
  /** The custom hex when the theme is `custom`. */
  customColor: string | null;
}

/** Resolve a stored `background` value (theme id, alias, legacy id or `custom:#hex`). */
export function resolveShareCardTheme(value: string | null | undefined): ResolvedShareCardTheme {
  const customHex = customBackgroundHex(value);
  if (customHex) {
    const ink = shareCardCustomInk(customHex);
    const dark = ink === '#ffffff';
    const base = getShareCardTheme('custom');
    return {
      ...base,
      css: customHex,
      fallbackColor: customHex,
      ink,
      linkInk: dark ? '#ffffff' : SHARE_CARD_LINK_INK_DARK,
      editInk: ink,
      orbitColor: dark ? ORBIT_LIGHT : ORBIT_DARK,
      isDark: dark,
      customColor: customHex,
    };
  }
  const id = typeof value === 'string' ? (THEME_ALIASES[value] ?? value) : undefined;
  const catalog = id && id !== 'custom' ? THEME_BY_ID.get(id as ShareCardThemeId) : undefined;
  if (catalog) return { ...catalog, customColor: null };
  const legacy = id ? LEGACY_SHARE_CARD_GRADIENTS[id] : undefined;
  if (legacy) {
    return {
      id: 'legacy',
      kind: 'legacy',
      artKey: null,
      css: legacy.css,
      fallbackColor: legacy.css.match(/#[0-9a-fA-F]{6}/g)?.[1] ?? '#7657f6',
      ink: legacy.isDark ? '#ffffff' : '#171719',
      linkInk: legacy.isDark ? SHARE_CARD_LINK_INK_LIGHT : SHARE_CARD_LINK_INK_DARK,
      editInk: legacy.isDark ? '#ffffff' : '#171719',
      orbitColor: legacy.isDark ? ORBIT_LIGHT : ORBIT_DARK,
      ring: null,
      isDark: legacy.isDark,
      gcc: false,
      pro: true,
      customColor: null,
    };
  }
  return { ...getShareCardTheme(SHARE_CARD_DEFAULT_BACKGROUND), customColor: null };
}

/** The catalog id a stored background value stands for (`custom` for any custom colour). */
export function shareCardThemeId(value: string | null | undefined): ShareCardThemeId {
  const resolved = resolveShareCardTheme(value);
  return resolved.id === 'legacy' ? SHARE_CARD_DEFAULT_BACKGROUND : resolved.id;
}

/** Resolve a stored `background` value to a renderable CSS string + contrast hint. */
export function resolveShareCardBackground(
  value: string | null | undefined,
): { background: string; isDark: boolean } {
  const t = resolveShareCardTheme(value);
  return { background: t.css, isDark: t.isDark };
}

/** Legacy layered-gradient colours, for the old Pro gradients' native renderer. */
export interface ShareCardGradient {
  top: string;
  mid: string;
  bottom: string;
  glow: string;
}

/**
 * Colours of a legacy `linear-gradient(...)` background for the native
 * ShareCardGradient. Returns null for anything that is not a CSS gradient.
 */
export function resolveShareCardGradient(background: string): ShareCardGradient | null {
  if (!background.includes('gradient')) return null;
  const hexes = background.match(/#[0-9a-fA-F]{3,6}/g);
  if (!hexes || hexes.length < 2) return null;
  const light = hexes[0];
  const mid = hexes[Math.floor(hexes.length / 2)] ?? light;
  const dark = hexes[hexes.length - 1];
  return { top: dark, mid, bottom: light, glow: light };
}

// ─── Silhouettes ─────────────────────────────────────────────

export const SHARE_CARD_PATTERNS: readonly ShareCardPattern[] = [
  'none',
  'signature',
  'cv',
  'orbit',
  'arch',
  'wave',
];
export const SHARE_CARD_FREE_PATTERNS: readonly ShareCardPattern[] = ['none', 'signature'];

/** Ink opacity of each silhouette on a regular theme; GCC themes use SHARE_CARD_GCC_SILHOUETTE_OPACITY. */
export const SHARE_CARD_SILHOUETTE_OPACITY: Record<ShareCardPattern, number> = {
  none: 0,
  signature: 0.055,
  cv: 0.075,
  // Orbit carries its alpha in the ring colour.
  orbit: 1,
  arch: 0.065,
  wave: 0.065,
};
export const SHARE_CARD_GCC_SILHOUETTE_OPACITY = 0.025;

export function normalizeShareCardPattern(value: unknown): ShareCardPattern {
  return typeof value === 'string' && (SHARE_CARD_PATTERNS as readonly string[]).includes(value)
    ? (value as ShareCardPattern)
    : SHARE_CARD_DEFAULT_PATTERN;
}

// ─── Call to action ──────────────────────────────────────────

export type ShareCardCtaKind = 'view' | 'download';
export type ShareCardCtaPreset = 'view' | 'download';

/**
 * Trim and migrate stored CTA wording. The retired "My CV" preset becomes
 * "View CV"; empty wording means the default (null).
 */
export function normalizeShareCardCta(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const migrated = trimmed === 'My CV' ? SHARE_CARD_DEFAULT_CTA : trimmed;
  return migrated.slice(0, SHARE_CARD_CTA_MAX);
}

/** Text equal to "download cv" (case and surrounding-space insensitive) downloads the PDF. */
export function isShareCardDownloadCta(label: string): boolean {
  return label.trim().toLowerCase() === SHARE_CARD_DOWNLOAD_CTA.toLowerCase();
}

/** Which preset the wording is, exactly (the free check mirrors the design repo). */
export function shareCardCtaPreset(label: string | null | undefined): ShareCardCtaPreset | null {
  const text = normalizeShareCardCta(label) ?? SHARE_CARD_DEFAULT_CTA;
  if (text === SHARE_CARD_DEFAULT_CTA) return 'view';
  if (text === SHARE_CARD_DOWNLOAD_CTA) return 'download';
  return null;
}

export interface ResolvedShareCardCta {
  /** The wording to print (English default when nothing is stored). */
  label: string;
  /** What the button does in the share sheet. */
  kind: ShareCardCtaKind;
  /** The preset this wording is, so the app can translate it; null = custom wording. */
  preset: ShareCardCtaPreset | null;
}

export function resolveShareCardCta(value: string | null | undefined): ResolvedShareCardCta {
  const label = normalizeShareCardCta(value) ?? SHARE_CARD_DEFAULT_CTA;
  return {
    label,
    kind: isShareCardDownloadCta(label) ? 'download' : 'view',
    preset: shareCardCtaPreset(label),
  };
}

// ─── Card link ───────────────────────────────────────────────

/** The favicon belongs only to Tabbio hosts: tabbio.com and its subdomains. */
export function isTabbioShareHost(hostname: string): boolean {
  const host = hostname.trim().toLowerCase();
  return host === 'tabbio.com' || host.endsWith('.tabbio.com');
}

/**
 * Validate a custom card link: http(s), no credentials, max 200 characters.
 * Returns the normalised href or null.
 */
export function parseShareCardUrl(value: string): string | null {
  const raw = value.trim();
  if (!raw || raw.length > SHARE_CARD_URL_MAX) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (url.username || url.password) return null;
  if (!url.hostname) return null;
  return url.href;
}

/** `undefined` = default link, `null` = removed, string = a valid custom link. */
export function normalizeShareCardUrl(value: unknown): string | null | undefined {
  if (value === null) return null;
  if (typeof value !== 'string') return undefined;
  if (!value.trim()) return null;
  return parseShareCardUrl(value) ?? undefined;
}

/** A URL as printed on the card: no protocol, no trailing slash. */
export function shareCardLinkDisplay(url: string): string {
  return url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

export interface ResolvedShareCardLink {
  /** The link the card advertises, or null when the row is hidden. */
  url: string | null;
  /** The printed text. */
  display: string | null;
  /** The card prints the owner's own profile link (nothing custom saved). */
  isDefault: boolean;
  /** The Tabbio favicon sits before the text. */
  showFavicon: boolean;
}

/**
 * Resolve the card's link row. The default profile link always carries the
 * favicon (it is a Tabbio link even on a dev host); a custom link only when
 * its host is tabbio.com or a subdomain. `hideBranding` hides the favicon.
 */
export function resolveShareCardLink(
  card: Pick<ShareCardConfig, 'cardUrl' | 'hideBranding'> | null | undefined,
  defaultUrl: string | null | undefined,
): ResolvedShareCardLink {
  const hidden = { url: null, display: null, isDefault: false, showFavicon: false };
  const custom = normalizeShareCardUrl(card?.cardUrl);
  if (custom === null) return hidden;
  const hideBranding = card?.hideBranding ?? false;
  if (typeof custom === 'string') {
    let tabbio = false;
    try {
      tabbio = isTabbioShareHost(new URL(custom).hostname);
    } catch {
      tabbio = false;
    }
    return {
      url: custom,
      display: shareCardLinkDisplay(custom),
      isDefault: false,
      showFavicon: tabbio && !hideBranding,
    };
  }
  const url = defaultUrl?.trim() || null;
  if (!url) return hidden;
  return {
    url,
    display: shareCardLinkDisplay(url),
    isDefault: true,
    showFavicon: !hideBranding,
  };
}

// ─── Config ──────────────────────────────────────────────────

/** Trim and hard-cap card text to its max length. */
export function clampShareCardText(value: string, max: number): string {
  const trimmed = value.replace(/\s+$/u, '');
  return trimmed.length > max ? trimmed.slice(0, max) : trimmed;
}

/** Default card config for a CV with no saved customization. */
export function defaultShareCardConfig(input?: { showPhoto?: boolean | null }): ShareCardConfig {
  return {
    background: SHARE_CARD_DEFAULT_BACKGROUND,
    backgroundImage: null,
    title: null,
    description: null,
    showPhoto: input?.showPhoto ?? true,
    hideBranding: false,
  };
}

/**
 * Validate + normalize an untrusted card value into a full ShareCardConfig.
 * Returns undefined when there is nothing to store (so existing rows that
 * never set a card stay untouched). Default-valued optional fields are
 * omitted so stored configs stay minimal and legacy shapes round-trip
 * byte-identical.
 */
export function normalizeShareCardConfig(value: unknown): ShareCardConfig | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const v = value as Record<string, unknown>;
  const pattern = normalizeShareCardPattern(v.pattern);
  const cta = normalizeShareCardCta(v.cta);
  const cardUrl = normalizeShareCardUrl(v.cardUrl);
  return {
    background:
      typeof v.background === 'string' && v.background.length <= 200
        ? v.background
        : SHARE_CARD_DEFAULT_BACKGROUND,
    backgroundImage:
      typeof v.backgroundImage === 'string' && v.backgroundImage.trim()
        ? v.backgroundImage.trim()
        : null,
    title:
      typeof v.title === 'string' && v.title.trim()
        ? clampShareCardText(v.title.trim(), SHARE_CARD_TITLE_MAX)
        : null,
    description:
      typeof v.description === 'string' && v.description.trim()
        ? clampShareCardText(v.description.trim(), SHARE_CARD_DESC_MAX)
        : null,
    showPhoto: typeof v.showPhoto === 'boolean' ? v.showPhoto : true,
    hideBranding: typeof v.hideBranding === 'boolean' ? v.hideBranding : false,
    ...(pattern !== SHARE_CARD_DEFAULT_PATTERN ? { pattern } : {}),
    ...(cta ? { cta } : {}),
    ...(cardUrl !== undefined ? { cardUrl } : {}),
  };
}

// ─── Free / paid matrix ──────────────────────────────────────

/**
 * What a card asks Premium for. Free may save: themes white/black/tabbio,
 * silhouettes none/signature, the CTA presets, name, headline and photo.
 */
export type ShareCardPaidChoice =
  | 'theme'
  | 'pattern'
  | 'cta'
  | 'cardUrl'
  | 'backgroundImage'
  | 'hideBranding';

export function isFreeShareCardTheme(background: string | null | undefined): boolean {
  const resolved = resolveShareCardTheme(background);
  return resolved.id !== 'legacy' && !resolved.pro;
}

/** The paid choices a card carries, in editor order. Empty = Free can save it as is. */
export function paidShareCardChoices(
  card: ShareCardConfig | null | undefined,
): ShareCardPaidChoice[] {
  if (!card) return [];
  const choices: ShareCardPaidChoice[] = [];
  if (!isFreeShareCardTheme(card.background)) choices.push('theme');
  if (!SHARE_CARD_FREE_PATTERNS.includes(normalizeShareCardPattern(card.pattern))) {
    choices.push('pattern');
  }
  if (shareCardCtaPreset(card.cta) === null) choices.push('cta');
  if (normalizeShareCardUrl(card.cardUrl) !== undefined) choices.push('cardUrl');
  if (card.backgroundImage?.trim()) choices.push('backgroundImage');
  if (card.hideBranding) choices.push('hideBranding');
  return choices;
}

/**
 * The Free version of a card: paid choices fall back (theme -> tabbio,
 * silhouette -> signature, link -> the profile link, wording -> "View CV",
 * no photo background, mark shown) while name, headline, photo and allowed
 * selections are kept.
 */
export function sanitizeShareCardForFree(card: ShareCardConfig): ShareCardConfig {
  const pattern = normalizeShareCardPattern(card.pattern);
  const cta = shareCardCtaPreset(card.cta) ? normalizeShareCardCta(card.cta) : null;
  const free: ShareCardConfig = {
    background: isFreeShareCardTheme(card.background)
      ? card.background
      : SHARE_CARD_DEFAULT_BACKGROUND,
    backgroundImage: null,
    title: card.title,
    description: card.description,
    showPhoto: card.showPhoto,
    hideBranding: false,
  };
  if (SHARE_CARD_FREE_PATTERNS.includes(pattern) && pattern !== SHARE_CARD_DEFAULT_PATTERN) {
    free.pattern = pattern;
  }
  if (cta && cta !== SHARE_CARD_DEFAULT_CTA) free.cta = cta;
  return free;
}

/**
 * Server- and client-side paywall coercion. When the user lacks the
 * `individual.custom_branding` entitlement, the card is reduced to its Free
 * version (sanitizeShareCardForFree). Returns the same object when allowed.
 */
export function coerceShareCardForEntitlement<T extends ShareCardConfig | null | undefined>(
  card: T,
  allowed: boolean,
): T {
  if (!card || allowed) return card;
  return sanitizeShareCardForFree(card) as T;
}

// ─── Full resolution for renderers ───────────────────────────

export interface ResolvedShareCard extends ResolvedShareCardTheme {
  /** The CSS background (same as `css`; kept for existing callers). */
  background: string;
  /** Custom background image URL (Pro), rendered with a dark scrim when set. */
  backgroundImage: string | null;
  pattern: ShareCardPattern;
  /** Opacity of the silhouette layer for this theme + pattern. */
  silhouetteOpacity: number;
  title: string;
  description: string;
  showPhoto: boolean;
  hideBranding: boolean;
  cta: ResolvedShareCardCta;
  /** The link row, resolved against `fallback.publicUrl`. */
  link: ResolvedShareCardLink;
}

/**
 * Fully resolve a (possibly null) card config against profile fallbacks for
 * rendering. Used identically by the OG image, native preview, and web preview.
 *
 * The subtitle line is a job title, not a bio: it resolves to the custom
 * description first, then the profile headline (`Profile.title`), and only
 * falls back to the professional summary when no headline is set: a summary
 * clamped to SHARE_CARD_DESC_MAX reads as a truncated mid-sentence fragment.
 */
export function resolveShareCard(
  card: ShareCardConfig | null | undefined,
  fallback: {
    title: string;
    /** Job title / professional headline: the preferred subtitle. */
    headline?: string | null;
    /** Professional summary: last-resort subtitle when there is no headline. */
    summary?: string | null;
    showPhoto?: boolean;
    /** The owner's public profile link: the default card link. */
    publicUrl?: string | null;
  },
): ResolvedShareCard {
  const backgroundImage = card?.backgroundImage?.trim() || null;
  const base = resolveShareCardTheme(card?.background);
  // A photo background always carries a dark scrim, so render light foreground.
  const themed: ResolvedShareCardTheme = backgroundImage
    ? {
        ...base,
        ink: '#ffffff',
        linkInk: SHARE_CARD_LINK_INK_LIGHT,
        editInk: '#ffffff',
        orbitColor: ORBIT_LIGHT,
        ring: null,
        isDark: true,
        gcc: false,
      }
    : base;
  const pattern = normalizeShareCardPattern(card?.pattern);
  const title = card?.title?.trim() || fallback.title;
  const description =
    card?.description?.trim() ||
    fallback.headline?.trim() ||
    fallback.summary?.trim() ||
    SHARE_CARD_DEFAULT_DESCRIPTION;
  const hideBranding = card?.hideBranding ?? false;
  return {
    ...themed,
    background: themed.css,
    backgroundImage,
    pattern,
    silhouetteOpacity:
      pattern === 'none'
        ? 0
        : themed.gcc
          ? SHARE_CARD_GCC_SILHOUETTE_OPACITY
          : SHARE_CARD_SILHOUETTE_OPACITY[pattern],
    title: clampShareCardText(title, SHARE_CARD_TITLE_MAX),
    description: clampShareCardText(description, SHARE_CARD_DESC_MAX),
    showPhoto: card?.showPhoto ?? fallback.showPhoto ?? true,
    hideBranding,
    cta: resolveShareCardCta(card?.cta),
    link: resolveShareCardLink(
      { cardUrl: card?.cardUrl, hideBranding },
      fallback.publicUrl,
    ),
  };
}

// ─── API schema ──────────────────────────────────────────────

const cardUrlSchema = z
  .string()
  .max(SHARE_CARD_URL_MAX)
  .refine((value) => !value.trim() || parseShareCardUrl(value) !== null, {
    message: 'cardUrl must be an http(s) URL without credentials',
  });

/** Zod schema for the card payload accepted by the API (all fields optional). */
export const shareCardConfigSchema = z.object({
  background: z.string().max(200).optional(),
  backgroundImage: z.string().max(2048).nullable().optional(),
  title: z.string().max(SHARE_CARD_TITLE_MAX).nullable().optional(),
  description: z.string().max(SHARE_CARD_DESC_MAX).nullable().optional(),
  showPhoto: z.boolean().optional(),
  hideBranding: z.boolean().optional(),
  // A string, not an enum: legacy pages/glow/grain values normalise to signature.
  pattern: z.string().max(32).optional(),
  cta: z.string().max(SHARE_CARD_CTA_MAX).nullable().optional(),
  cardUrl: cardUrlSchema.nullable().optional(),
});

export type ShareCardConfigInput = z.infer<typeof shareCardConfigSchema>;
